/**
 * useConfiggr.ts - React hook for consuming Configgr settings
 * 
 * Provides a simple interface for packages to read and react to their settings.
 * 
 * USAGE:
 * ```typescript
 * import { useConfiggr } from '@opcentral/configgr';
 * 
 * function MyComponent() {
 *   const { getSetting, updateSetting, watchSetting } = useConfiggr('my-package');
 *   
 *   const enabled = getSetting('enabled', true);
 *   const timeout = getSetting('timeout', 5000);
 *   
 *   // React to setting changes
 *   watchSetting('enabled', (newValue) => {
 *     console.log('Enabled changed to:', newValue);
 *   });
 *   
 *   return <div>Enabled: {enabled}</div>;
 * }
 * ```
 * 
 * CHANGE HISTORY:
 * - 251106_1344 [pending] danielbeer: Initial creation
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ConfiggrManager } from '../core/ConfiggrManager';

// Global ConfiggrManager instance
let globalConfiggrManager: ConfiggrManager | null = null;

/**
 * Initialize the global Configgr instance
 * Should be called once at app startup
 */
export function initializeConfiggr(manager: ConfiggrManager): void {
  globalConfiggrManager = manager;
}

/**
 * Get the global Configgr instance
 */
export function getConfiggrManager(): ConfiggrManager | null {
  return globalConfiggrManager;
}

export interface UseConfiggrOptions {
  /**
   * Custom ConfiggrManager instance (optional)
   * If not provided, uses the global instance
   */
  manager?: ConfiggrManager;
  
  /**
   * Auto-refresh interval in ms (optional)
   * If provided, will re-read settings at this interval
   */
  refreshInterval?: number;
}

export interface UseConfiggrReturn {
  /**
   * Get a setting value with optional default
   */
  getSetting: <T = any>(key: string, defaultValue?: T) => T;
  
  /**
   * Update a setting value
   */
  updateSetting: (key: string, value: any) => void;
  
  /**
   * Watch a setting for changes
   * Returns cleanup function
   */
  watchSetting: (key: string, callback: (value: any) => void) => () => void;
  
  /**
   * Get all settings for this module
   */
  getAllSettings: () => Record<string, any>;
  
  /**
   * Force refresh settings from storage
   */
  refresh: () => void;
}

/**
 * Hook for consuming Configgr settings in React components
 * 
 * @param moduleId - The module ID (e.g., 'my-package')
 * @param options - Optional configuration
 */
export function useConfiggr(
  moduleId: string,
  options: UseConfiggrOptions = {}
): UseConfiggrReturn {
  const { manager, refreshInterval } = options;
  const configgr = manager || globalConfiggrManager;
  
  if (!configgr) {
    console.warn('[useConfiggr] No ConfiggrManager instance available. Call initializeConfiggr() first.');
  }
  
  // Track setting values for reactivity
  const [, forceUpdate] = useState({});
  const watchersRef = useRef<Map<string, Set<(value: any) => void>>>(new Map());
  
  // Force refresh
  const refresh = useCallback(() => {
    forceUpdate({});
  }, []);
  
  // Auto-refresh if interval specified
  useEffect(() => {
    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(refresh, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, refresh]);
  
  // Get setting value
  const getSetting = useCallback(<T = any>(key: string, defaultValue?: T): T => {
    if (!configgr) return defaultValue as T;
    
    const fullPath = `${moduleId}.${key}`;
    const value = configgr.getSetting(fullPath);
    return value !== undefined ? value : defaultValue as T;
  }, [configgr, moduleId]);
  
  // Update setting value
  const updateSetting = useCallback((key: string, value: any) => {
    if (!configgr) return;
    
    const fullPath = `${moduleId}.${key}`;
    configgr.updateSetting(fullPath, value);
    
    // Notify watchers
    const watchers = watchersRef.current.get(key);
    if (watchers) {
      watchers.forEach(callback => callback(value));
    }
    
    // Force re-render
    refresh();
  }, [configgr, moduleId, refresh]);
  
  // Watch setting for changes
  const watchSetting = useCallback((key: string, callback: (value: any) => void): (() => void) => {
    if (!watchersRef.current.has(key)) {
      watchersRef.current.set(key, new Set());
    }
    
    const watchers = watchersRef.current.get(key)!;
    watchers.add(callback);
    
    // Return cleanup function
    return () => {
      watchers.delete(callback);
      if (watchers.size === 0) {
        watchersRef.current.delete(key);
      }
    };
  }, []);
  
  // Get all settings for this module
  const getAllSettings = useCallback((): Record<string, any> => {
    if (!configgr) return {};
    
    // Access through public method instead of private property
    const moduleSettings: Record<string, any> = {};
    const module = configgr.getModule(moduleId);
    
    if (module?.settings) {
      Object.keys(module.settings).forEach(key => {
        moduleSettings[key] = getSetting(key, module.settings[key].defaultValue);
      });
    }
    
    return moduleSettings;
  }, [configgr, moduleId, getSetting]);
  
  // Memoize return object to prevent infinite re-renders
  return useMemo(() => ({
    getSetting,
    updateSetting,
    watchSetting,
    getAllSettings,
    refresh
  }), [getSetting, updateSetting, watchSetting, getAllSettings, refresh]);
}

export default useConfiggr;
